import type { SaleDetail } from '../../sales/domain/salesTypes';
import { DEFAULT_PAYMENT_METHOD_LABELS } from '../../sales/infra/salesPaymentLabels';
import type { CartItem } from '../../pos/store/usePosStore';
import type { PosDraftPayment } from '../../pos/domain/posTypes';
import type { CartEvaluationResult } from '../../promotions/domain/promotionTypes';
import { showError, showInfo } from '../../shared/ui/appToast';
import {
  mapPosCheckoutToReceiptPayload,
  mapSaleDetailToReceiptPayload,
} from './buildSaleReceiptPayload';
import { printSaleReceiptJob, shouldAutoPrintSaleReceipt } from './printOrchestrator';
import { resolveSaleReceiptBranding } from './resolveSaleReceiptBranding';

export async function printSaleReceiptFromDetail(args: {
  detail: SaleDetail;
  paymentMethodNames: Record<string, string>;
  diningTableName?: string | null;
  /** Capability `kitchen_tickets` de la org — copy handoff QSR del ticket. */
  kitchenTickets?: boolean;
  /** Leyendas extra bajo el pie (ej. "Factura en emisión…"). */
  footerNotes?: string[];
  trackingUrl?: string | null;
}): Promise<boolean> {
  const branding = resolveSaleReceiptBranding(args.detail.branchId);
  const payload = mapSaleDetailToReceiptPayload({
    detail: args.detail,
    paymentMethodNames: args.paymentMethodNames,
    branding,
    diningTableName: args.diningTableName,
    kitchenTickets: args.kitchenTickets,
    footerNotes: args.footerNotes,
    trackingUrl: args.trackingUrl,
  });
  const result = await printSaleReceiptJob(args.detail.branchId, payload);
  if (!result.ok) {
    showError('No se pudo imprimir', result.error);
    return false;
  }
  if (result.warning) showInfo('Impresión', result.warning);
  return true;
}

type SaleReceiptCheckoutArgs = {
  saleId: string;
  branchId: string;
  cart: CartItem[];
  paymentDrafts: PosDraftPayment[];
  cartEvaluation?: CartEvaluationResult | null;
  tipAmount?: number;
  cashierDisplayName?: string | null;
  customerName?: string | null;
  diningTableName?: string | null;
  paymentMethodNames?: Record<string, string>;
  counterNumber?: number | null;
  pickupName?: string | null;
  fulfillmentType?: string | null;
  /** Capability `kitchen_tickets` de la org — copy handoff QSR del ticket. */
  kitchenTickets?: boolean;
  /** Leyendas extra bajo el pie (ej. "Factura en emisión…"). */
  footerNotes?: string[];
  trackingUrl?: string | null;
};

export async function printSaleReceiptAfterCheckout(args: SaleReceiptCheckoutArgs): Promise<void> {
  const auto = await shouldAutoPrintSaleReceipt(args.branchId);
  if (!auto) return;
  await printSaleReceiptFromCheckout(args);
}

/** Impresión manual post-cobro (ignora autoPrint; respeta mode off). */
export async function printSaleReceiptFromCheckout(args: SaleReceiptCheckoutArgs): Promise<boolean> {
  const branding = resolveSaleReceiptBranding(args.branchId);
  const payload = mapPosCheckoutToReceiptPayload({
    saleId: args.saleId,
    branchName: branding.branchName,
    organizationName: branding.organizationName,
    cart: args.cart,
    paymentDrafts: args.paymentDrafts,
    paymentMethodNames: args.paymentMethodNames ?? DEFAULT_PAYMENT_METHOD_LABELS,
    cartEvaluation: args.cartEvaluation,
    tipAmount: args.tipAmount,
    cashierDisplayName: args.cashierDisplayName,
    customerName: args.customerName,
    diningTableName: args.diningTableName,
    counterNumber: args.counterNumber,
    pickupName: args.pickupName,
    fulfillmentType: args.fulfillmentType,
    kitchenTickets: args.kitchenTickets,
    footerNotes: args.footerNotes,
    trackingUrl: args.trackingUrl,
  });

  const result = await printSaleReceiptJob(args.branchId, payload);
  if (!result.ok) {
    showError('No se pudo imprimir', result.error);
    return false;
  }
  if (result.warning) showInfo('Impresión', result.warning);
  return true;
}
